import './SpaceTypes.scss';

import { SvgIcon } from '@mui/material';
import ButtonField from '../../Components/Button/ButtonField';
import Table from '../../Components/Table/Table';
import CustomToolbar from '../../Components/Table/components/CustomToolbar';
import TableSearch from '../../Components/Table/components/TableSearch';
import { ISpaceTypesList, SpaceTypesProps } from './ISpaceTypes';
import {
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import { ReactComponent as Add } from '../../Assets/Images/Add.svg';
import { Columns } from '../../Components/Table/ITable';
import { useAppDispatch, useAppSelector } from '../../Redux/Store/Hooks';
import { getSpaceTypes } from '../../Redux/Slicers/SpaceTypes/SpaceTypesSlicer';
import {
  GridActionsCellItem,
  GridRowParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { ReactComponent as Edit } from '../../Assets/Images/Edit.svg';
import { ReactComponent as Delete } from '../../Assets/Images/Delete.svg';
import Dialog from '../../Components/Dialog/Dialog';
import IconButtonField from '../../Components/Button/IconButtonField';
import { ReactComponent as Cancel } from '../../Assets/Images/Cancel.svg';
import SpaceTypesForm from './SpaceTypesForm';
import { FormikProps } from 'formik';
import {
  clearUpdateSpaceTypesState,
  SpaceTypesUpdateableData,
} from '../../Redux/Slicers/SpaceTypes/UpdateSpaceTypesSlicer';
import ResponseDialog from '../../Components/ResponseDialog/ResponseDialog';
import DeleteDialog from '../../Components/DeleteDialog/DeleteDialog';
import {
  clearDeleteSpaceTypes,
  deleteSpaceTypes,
} from '../../Redux/Slicers/SpaceTypes/DeleteSpaceTypesSlice';
import { clearAddSpaceTypesState } from '../../Redux/Slicers/SpaceTypes/AddSpaceTypesSlicer';
import { useLocation } from 'react-router-dom';
import { getDesignation } from '../../Redux/Slicers/Designation/DesignationSlicer';

const SpaceTypes: FunctionComponent<SpaceTypesProps> = () => {
  const dispatch = useAppDispatch();
  const { data } = useAppSelector((state) => state?.SpaceTypes);
  const [open, setOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteableName, setDeleteableName] = useState<string>('');
  const [deleteableId, setDeleteableId] = useState<number>(0);
  const [formik, setFormik] = useState<FormikProps<ISpaceTypesList>>();
  const { data: updateSpaceTypes, updateableDate } = useAppSelector(
    (state) => state?.UpdateSpaceTypes
  );
  const { data: addSpaceTypes } = useAppSelector(
    (state) => state?.AddSpaceTypes
  );
  const { data: deleteSpaceTypesData } = useAppSelector(
    (state) => state?.DeleteSpaceTypes
  );
  const location = useLocation();
  const pathnameParts = location.pathname.split('/');
  const id = pathnameParts[pathnameParts.length - 1];
  const isUpdateable =
    updateableDate && Object.keys(updateableDate).length !== 0;
  const column: Columns = [
    {
      field: 'index',
      renderHeader: () => <b>S.No</b>,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      maxWidth: 190,
    },
    {
      field: 'name',
      renderHeader: () => <b>Name</b>,
      flex: 1,
      type: 'string',
      maxWidth: 250,
    },

    {
      field: 'working_time',
      renderHeader: () => <b>Working Time</b>,
      flex: 2,
      type: 'string',
      valueGetter: (params: GridValueGetterParams) => {
        const workingHours = (params.row as ISpaceTypesList).working_hours;
        if (!workingHours) return '-';

        const entries = Object.entries(workingHours) as [
          string,
          { open: boolean; start: string; end: string }
        ][];

        return entries
          .map(([day, time]) => {
            const label = day.charAt(0).toUpperCase() + day.slice(1);
            return `${label}: ${
              time.open ? `Reserved (${time.start}-${time.end})` : 'Regular'
            }`;
          })
          .join(' | ');
      },
    },

    {
      field: 'restriction',
      renderHeader: () => <b>Restriction</b>,
      flex: 1,
      type: 'number',
      maxWidth: 250,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.restriction === 'allow'
          ? 'Only allow certain user designations'
          : params.row.restriction === ''
          ? 'None'
          : 'Disallow certain user designations';
      },
    },
    {
      field: 'action',
      renderHeader: () => <b>Action</b>,
      flex: 1,
      type: 'actions',
      getActions: (params: GridRowParams<any>) => [
        <GridActionsCellItem
          key="Edit"
          label="Edit"
          icon={<Edit className="action_icons" />}
          onClick={() => {
            dispatch(getDesignation(id));
            dispatch(SpaceTypesUpdateableData(params.row));
            setOpen(true);
          }}
        />,
        <GridActionsCellItem
          key="Delete"
          label="Delete"
          icon={<Delete className="action_icons" />}
          onClick={() => {
            setDeleteOpen(true);
            setDeleteableName(params.row.name ?? '');
            setDeleteableId(params.row.id!);
          }}
        />,
      ],
      cellClassName: 'action_cell',
    },
  ];

  useEffect(() => {
    dispatch(getSpaceTypes(id));
  }, [dispatch, id, updateSpaceTypes, addSpaceTypes, deleteSpaceTypesData]);

  useEffect(() => {
    return () => {
      dispatch(clearAddSpaceTypesState());
      dispatch(clearUpdateSpaceTypesState());
      dispatch(clearDeleteSpaceTypes());
    };
  }, [dispatch]);

  const data1 = data?.response?.data;
  const masterData = Array.isArray(data1)
    ? data1.map((master, index) => ({
        ...master,
        id: master._id,
        index: index + 1,
      }))
    : [];

  const spaceTypesForm = (data: FormikProps<ISpaceTypesList>) => {
    setFormik(data);
  };

  return (
    <>
      <Table
        rows={masterData ?? []}
        columns={column}>
        <CustomToolbar>
          <h6>User SpaceTypes</h6>
          <TableSearch />
          <ButtonField
            className="add_button"
            onClick={() => setOpen(true)}>
            <SvgIcon
              component={Add}
              inheritViewBox
            />
            Add New
          </ButtonField>
        </CustomToolbar>
      </Table>

      <Dialog
        open={open}
        dialogClassName="master_dialog"
        dialogTitleClassName="master_dialog_title"
        Dialog_Title={
          <>
            {isUpdateable ? 'Edit' : 'Add New'}
            <IconButtonField onClick={() => setOpen(false)}>
              <SvgIcon
                component={Cancel}
                inheritViewBox
              />
            </IconButtonField>
          </>
        }
        Dialog_Content={<SpaceTypesForm SpaceTypesForm={spaceTypesForm} />}
        dialogActionClassName="master_dialog_action"
        Dialog_Actions={
          <>
            <ButtonField
              className="cancel_button"
              onClick={() => setOpen(false)}>
              Cancel
            </ButtonField>
            <ButtonField
              className="add_button"
              onClick={() => formik?.handleSubmit()}>
              {isUpdateable ? 'Update' : 'Save'}
            </ButtonField>
          </>
        }
      />

      <ResponseDialog
        response={addSpaceTypes}
        parentDialog={setOpen}
      />
      <ResponseDialog
        response={updateSpaceTypes}
        parentDialog={setOpen}
      />

      <DeleteDialog
        deleteableName={deleteableName}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        deleteFunction={() => dispatch(deleteSpaceTypes(deleteableId))}
        response={deleteSpaceTypesData}
      />
    </>
  );
};

export default SpaceTypes;
